import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, Camera, ArrowLeft } from 'lucide-react';

const AuthError = () => {
  const [searchParams] = useSearchParams();
  const errorMessage = searchParams.get('error');
  
  // Default error message if none provided
  const displayMessage = errorMessage || "We couldn't connect your photo platform account. Please try again.";
  
  // Check if this is an account already connected error
  const isAccountConflict = errorMessage && errorMessage.toLowerCase().includes('already connected');
  const isEmailRequired = errorMessage && errorMessage.toLowerCase().includes('email address');
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-lg w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Authentication Failed
          </h2>
          <div className="mt-4 text-center">
            <p className="text-base text-gray-700 mb-4">{displayMessage}</p>
            
            {/* Specific help for account conflicts */}
            {isAccountConflict && (
              <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <h3 className="text-sm font-semibold text-yellow-800 mb-2">What can I do?</h3>
                <ul className="text-sm text-yellow-700 text-left space-y-1">
                  <li>• Use a different photo platform account</li>
                  <li>• Or sign in with the original email address</li>
                  <li>• Contact support if you need to transfer accounts</li>
                </ul>
              </div>
            )}
            
            {/* Specific help for email required */}
            {isEmailRequired && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="text-sm font-semibold text-blue-800 mb-2">Next Steps</h3>
                <p className="text-sm text-blue-700">
                  Please go to the home page and start the signup process by entering your email address first.
                </p>
              </div>
            )}
            
            {errorMessage && !isAccountConflict && !isEmailRequired && (
              <p className="text-xs text-gray-500 mt-4">
                If this error persists, please contact support.
              </p>
            )}
          </div>
        </div>
        
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            <Camera className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              What went wrong?
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              There might have been an issue with the authentication process. 
              This could be due to network issues, expired tokens, or user cancellation.
            </p>
            
            <div className="space-y-4">
              <Link
                to="/login"
                className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Try Again
              </Link>
              
              <div className="text-xs text-gray-500">
                If the problem persists, please check your internet connection and try again later.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthError;
